import { useState, useEffect, useRef, RefObject, useCallback } from "react";

interface UseScrollHighlightOptions {
  enabled?: boolean;
}

interface UseScrollHighlightReturn {
  activeIndex: number | null;
  isTouchDevice: boolean;
  itemRefs: RefObject<(HTMLElement | null)[]>;
}

export const useScrollHighlight = (options: UseScrollHighlightOptions = {}): UseScrollHighlightReturn => {
  const { enabled = true } = options;
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);
  const rafRef = useRef<number | null>(null);
  const lastScrollTime = useRef<number>(0);
  // Detect touch device - use matchMedia with slight delay to avoid blocking initial paint
  useEffect(() => {
    // Use requestIdleCallback or setTimeout to defer detection after initial paint
    const detectTouch = () => {
      const coarsePointer = window.matchMedia('(pointer: coarse)');
      setIsTouchDevice(coarsePointer.matches);
    };
    
    // Defer to avoid blocking initial render
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(detectTouch, { timeout: 200 });
    } else {
      setTimeout(detectTouch, 50);
    }
  }, []);

  // Throttled scroll handler using RAF to prevent forced reflows
  const findMostCenteredItem = useCallback(() => {
    // Throttle to max 60fps
    const now = performance.now();
    if (now - lastScrollTime.current < 16) return;
    lastScrollTime.current = now;

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(() => {
      const viewportCenter = window.innerHeight / 2;
      let closestIndex: number | null = null;
      let closestDistance = Infinity;

      itemRefs.current.forEach((ref, index) => {
        if (!ref) return;
        const rect = ref.getBoundingClientRect();
        const itemCenter = rect.top + rect.height / 2;
        const distance = Math.abs(itemCenter - viewportCenter);
        
        if (rect.bottom > 0 && rect.top < window.innerHeight && distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      setActiveIndex(closestIndex);
    });
  }, []);

  // Scroll-based highlighting for touch devices only
  useEffect(() => {
    if (!isTouchDevice || !enabled) return;

    window.addEventListener('scroll', findMostCenteredItem, { passive: true });
    
    // Delay initial check significantly to avoid forced reflow during initial render
    // Use requestIdleCallback for better timing when browser is idle
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let idleCallbackId: number | null = null;
    
    if ('requestIdleCallback' in window) {
      idleCallbackId = (window as any).requestIdleCallback(() => {
        findMostCenteredItem();
      }, { timeout: 500 });
    } else {
      timeoutId = setTimeout(findMostCenteredItem, 300);
    }

    return () => {
      window.removeEventListener('scroll', findMostCenteredItem);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (timeoutId) clearTimeout(timeoutId);
      if (idleCallbackId && 'cancelIdleCallback' in window) {
        (window as any).cancelIdleCallback(idleCallbackId);
      }
    };
  }, [isTouchDevice, enabled, findMostCenteredItem]);

  return { activeIndex, isTouchDevice, itemRefs };
};